import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { openCalendlyDemo } from '@/lib/analytics';
import { formatPostDate, type PostMeta } from '@/lib/blog';
import { FOUNDER, FOUNDER_PERSON_ID } from '@/lib/founder';

interface BlogPostLayoutProps {
  post: PostMeta;
  children: ReactNode;
}

const BlogPostLayout = ({ post, children }: BlogPostLayoutProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const url = `https://ezcloud.ai/blog/${post.slug}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Person',
      '@id': FOUNDER_PERSON_ID,
      name: post.author || FOUNDER.name,
      url: 'https://ezcloud.ai/about',
    },
    publisher: {
      '@type': 'Organization',
      name: 'EZ Cloud',
      logo: { '@type': 'ImageObject', url: 'https://ezcloud.ai/ezcloud-logo-v2.png' },
    },
    mainEntityOfPage: url,
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${post.title} | EZ Cloud`}
        description={post.description}
        canonical={url}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Blog', url: 'https://ezcloud.ai/blog' },
          { name: post.title, url },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          All posts
        </Link>

        <header className="mb-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {(post.tags || []).map((t) => (
              <span
                key={t}
                className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-5">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" aria-hidden />
              {formatPostDate(post.date)}
            </span>
            {post.readingTime && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" aria-hidden />
                {post.readingTime}
              </span>
            )}
            {post.author && <span>By {post.author}</span>}
          </div>
        </header>

        <article
          className="prose prose-lg max-w-none
            prose-headings:text-gray-900 prose-headings:font-bold
            prose-p:text-gray-700 prose-li:text-gray-700
            prose-strong:text-gray-900
            prose-a:text-primary prose-a:font-medium hover:prose-a:text-accent"
        >
          {children}
        </article>

        {/* Author box — real, credentialed author (E-E-A-T) */}
        <aside className="mt-12 flex items-start gap-4 rounded-2xl border border-gray-200 bg-gray-50 p-6">
          <img
            src={FOUNDER.photo}
            alt={FOUNDER.photoAlt}
            width={48}
            height={48}
            loading="lazy"
            className="flex-shrink-0 w-12 h-12 rounded-full object-cover border border-gray-200"
          />
          <div>
            <div className="font-semibold text-gray-900">{post.author || FOUNDER.name}</div>
            <div className="text-sm text-gray-500 mb-2">
              {FOUNDER.role}, {FOUNDER.company}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{FOUNDER.shortBio}</p>
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-accent transition-colors mt-2"
            >
              More about Andrew
              <ArrowRight className="w-3.5 h-3.5" aria-hidden />
            </Link>
          </div>
        </aside>

        {/* CTA */}
        <div className="mt-14 rounded-2xl bg-hero-gradient text-white p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">See it against your Oracle AP</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Book a 30-minute walkthrough — we'll run a real exception from supplier email to Oracle
            posting, on Fusion or EBS.
          </p>
          <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
            Book an Oracle AP demo
            <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default BlogPostLayout;
